import { useAlert } from '@/v2/composables/alert/alert'
import { get, MaybeRefOrGetter, ReadonlyRefOrGetter, toValue } from '@vueuse/core'
import { watch } from 'vue'
import { computed, onBeforeMount } from 'vue'
import { ref, Ref } from 'vue'

export function useGetEndpoint<In extends unknown[] = unknown[], Out = unknown>(
  endpoint: (...args: In) => Promise<Out>,
  params: ReadonlyRefOrGetter<In | undefined>,
  options: {
    lazy?: boolean
    inert?: boolean
    onError?: (error: unknown) => void | Promise<void>
    onSuccess?: (data: Out) => void | Promise<void>
  } = {},
) {
  const alert = useAlert()

  const data = ref<Out>()
  const loading = ref(false)
  const error = ref(false)
  const ready = computed(() => !loading.value && !error.value && data.value !== undefined)

  async function fetch(params: In) {
    loading.value = true
    error.value = false
    try {
      data.value = await endpoint(...params)
      options.onSuccess?.(data.value)
    } catch (err: unknown) {
      error.value = true
      if (options.onError) {
        await options.onError(err)
      } else {
        alert.error({ error: err })
      }
    } finally {
      loading.value = false
    }
  }

  if (!options.lazy) {
    onBeforeMount(() => {
      const paramsValue = toValue(params)
      if (paramsValue === undefined) {
        return
      }
      fetch(paramsValue)
    })
  }

  if (!options.inert) {
    watch(
      params,
      (paramsValue) => {
        if (paramsValue === undefined) {
          return
        }
        fetch(paramsValue)
      },
      { deep: true },
    )
  }

  async function refresh() {
    const paramsValue = toValue(params)
    if (paramsValue === undefined) {
      return
    }
    await fetch(paramsValue)
  }

  return {
    data,
    loading,
    error,
    ready,
    refresh,
    fetch,
  }
}
